import { generatePath, type PathParam } from 'react-router-dom';
import { type TFunction } from 'i18next';

import { FolderType, TalentCreateDTOType } from '@/api/generated';

type ParamObject = {
  [key in PathParam<string>]: string | null;
};
type SearchObject = string | string[][] | Record<string, string | undefined> | undefined; // type from URLSearchParams

export enum SETTINGS_STEP_KEYS {
  BASIC_INFO = 'basic-info',
  ACCESS_AND_ROLES = 'access-and-roles',
  ROLES_AND_MEMBERS = 'roles-and-members',
  PROCESS_SETTINGS = 'process-settings',
  IDENTIFICATION_AND_NORMS = 'identification-and-norms',
  NORMS = 'norms'
}

export enum PROCESS_SETTINGS_STEP_KEYS {
  LOCATION = 'location',
  BASIC_INFO = 'basic-info',
  TESTS = 'tests',
  TALENTS = 'talents',
  PROCESS_DECISION = 'process-decision',
  AUTO_DECISION_MAKER = 'auto-decision-maker',
  SETTINGS = 'settings',
  SETTINGS_TALENT_REGISTRATION = 'settings-talent-registration',
  SETTINGS_ACCESS_INVITATION = 'settings-access-invitation',
  SETTINGS_TEST_PROPERTIES = 'settings-test-properties',
  SETTINGS_MESSAGES = 'settings-messages'
}

export enum TALENT_REGISTRATION_FORM_TABS {
  INTERNAL = 'internal',
  EXTERNAL = 'external'
}

export enum ORGANIZATION_ITEM_PATH_TYPE {
  ORGANIZATION = 'organization',
  SUB_ORGANIZATION = 'suborganization',
  FOLDER = 'folder',
  SUB_FOLDER = 'subfolder'
}

export const TALENT_REGISTRATION_FORM_TABS_LIST = [
  TALENT_REGISTRATION_FORM_TABS.EXTERNAL,
  TALENT_REGISTRATION_FORM_TABS.INTERNAL
];

export const TALENT_REGISTRATION_FORM_LANGUAGES = ['en-US', 'he-IL', 'es-419'];
export const TALENT_REGISTRATION_FORM_DEFAULT_LANGUAGE = TALENT_REGISTRATION_FORM_LANGUAGES[0];

export const CREATE_MODAL_PARAM = 'createModal';
export const EDIT_MODAL_PARAM = 'editModal';
export const DEFAULT_VALUES_PARAM = 'defaultValues';
export const DEFAULT_TYPE = 'defaultValues.type';
export const DEFAULT_ORGANIZATION_SLUG = 'defaultValues.organizationSlug';
export const DEFAULT_LOCATION_SLUG = 'defaultValues.locationSlug';

const ORGANIZATION_ITEM_PATHS = {
  [FolderType.FOLDER]: 'folder',
  [FolderType.SUB_FOLDER]: 'subfolder',
  [FolderType.SUB_ORGANIZATION]: 'suborganization'
};

const toOrganizationItemPath = (itemType: FolderType) =>
  ORGANIZATION_ITEM_PATHS[itemType as keyof typeof ORGANIZATION_ITEM_PATHS];

export enum ROUTER_PATHS {
  LOGIN = '/login',
  HOME = '/',
  ORGANIZATIONS = '/organizations',
  ORGANIZATION = '/organizations/:organizationSlug',
  ORGANIZATION_ITEMS = '/organizations/:organizationSlug/items',
  ORGANIZATION_TAB_GENERIC = '/organizations/:organizationSlug/:tabKey',

  ORGANIZATION_SETTINGS = '/organizations/:organizationSlug/settings',
  ORGANIZATION_SETTINGS_GENERIC = '/organizations/:organizationSlug/settings/:stepKey',
  ORGANIZATION_SETTINGS_BASIC_INFO = `/organizations/:organizationSlug/settings/${SETTINGS_STEP_KEYS.BASIC_INFO}`,
  ORGANIZATION_SETTINGS_ROLES_AND_MEMBERS = `/organizations/:organizationSlug/settings/${SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS}`,
  ORGANIZATION_SETTINGS_PROCESS_SETTINGS = `/organizations/:organizationSlug/settings/${SETTINGS_STEP_KEYS.PROCESS_SETTINGS}`,
  ORGANIZATION_SETTINGS_IDENTIFICATION_AND_NORMS = `/organizations/:organizationSlug/settings/${SETTINGS_STEP_KEYS.IDENTIFICATION_AND_NORMS}`,
  ORGANIZATION_SETTINGS_TALENT_REGISTRATION_FORM = `/organizations/:organizationSlug/settings/${SETTINGS_STEP_KEYS.PROCESS_SETTINGS}/talent-registration-form/:activeTab`,

  ORGANIZATION_ITEM_SETTINGS_GENERIC = '/organizations/:organizationSlug/item-settings/:locationType/:stepKey',

  SUBORGANIZATION_SETTINGS = '/organizations/:organizationSlug/item-settings/suborganization',
  SUBORGANIZATION_SETTINGS_GENERIC = '/organizations/:organizationSlug/item-settings/suborganization/:stepKey',
  SUBORGANIZATION_SETTINGS_BASIC_INFO = `/organizations/:organizationSlug/item-settings/suborganization/${SETTINGS_STEP_KEYS.BASIC_INFO}`,
  SUBORGANIZATION_SETTINGS_ROLES_AND_MEMBERS = `/organizations/:organizationSlug/item-settings/suborganization/${SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS}`,
  SUBORGANIZATION_SETTINGS_PROCESS_SETTINGS = `/organizations/:organizationSlug/item-settings/suborganization/${SETTINGS_STEP_KEYS.PROCESS_SETTINGS}`,
  SUBORGANIZATION_SETTINGS_NORMS = `/organizations/:organizationSlug/item-settings/suborganization/${SETTINGS_STEP_KEYS.NORMS}`,
  SUBORGANIZATION_SETTINGS_TALENT_REGISTRATION_FORM = `/organizations/:organizationSlug/item-settings/suborganization/${SETTINGS_STEP_KEYS.PROCESS_SETTINGS}/talent-registration-form/:activeTab`,

  FOLDER_SETTINGS = '/organizations/:organizationSlug/item-settings/folder',
  FOLDER_SETTINGS_GENERIC = '/organizations/:organizationSlug/item-settings/folder/:stepKey',
  FOLDER_SETTINGS_BASIC_INFO = `/organizations/:organizationSlug/item-settings/folder/${SETTINGS_STEP_KEYS.BASIC_INFO}`,
  FOLDER_SETTINGS_ROLES_AND_MEMBERS = `/organizations/:organizationSlug/item-settings/folder/${SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS}`,

  SUBFOLDER_SETTINGS = '/organizations/:organizationSlug/item-settings/subfolder',
  SUBFOLDER_SETTINGS_GENERIC = '/organizations/:organizationSlug/item-settings/subfolder/:stepKey',
  SUBFOLDER_SETTINGS_BASIC_INFO = `/organizations/:organizationSlug/item-settings/subfolder/${SETTINGS_STEP_KEYS.BASIC_INFO}`,
  SUBFOLDER_SETTINGS_ROLES_AND_MEMBERS = `/organizations/:organizationSlug/item-settings/subfolder/${SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS}`,

  ORGANIZATION_PROCESSES = '/organizations/:organizationSlug/processes',
  ORGANIZATION_TALENTS = '/organizations/:organizationSlug/talents',
  ORGANIZATION_HISTORY = '/organizations/:organizationSlug/history',
  PROCESSES = '/processes',
  PROCESS_NEW = '/process/new',
  PROCESS = '/processes/:processId',
  PROCESS_SETTINGS_FORM = '/processes/:processId/settings',
  TALENT = '/talents/:talentId',
  TALENTS = '/talents',
  TALENTS_CREATE = '/talents/create',
  TESTS = '/tests',
  TALENT_TEST_SESSION = '/talents/:talentId/test-sessions/:testSessionId',

  USERS = '/users',
  USERS_CLIENT = '/users/client',
  USERS_MIDOT = '/users/midot',
  USERS_SETTINGS_GENERIC = '/user-settings/:userId/:stepKey',
  USERS_SETTINGS = '/user-settings/:userId',
  USERS_SETTINGS_BASIC_INFO = `/user-settings/:userId/${SETTINGS_STEP_KEYS.BASIC_INFO}`,
  USERS_SETTINGS_ACCESS_AND_ROLES = `/user-settings/:userId/${SETTINGS_STEP_KEYS.ACCESS_AND_ROLES}`,

  ROLES = '/roles',
  ONBOARDING = '/onboarding'
}

const normalizeSearchParams = (searchParams: SearchObject = {}) =>
  Object.fromEntries(Object.entries(searchParams).filter(([, value]) => typeof value !== 'undefined'));

export const asPath = (basePath: string, params?: ParamObject, searchParams?: SearchObject): string => {
  const path = generatePath(basePath, params);

  const searchParamsString = new URLSearchParams(normalizeSearchParams(searchParams)).toString();

  return [path, searchParamsString].filter(Boolean).join('?');
};

export const toOrganizationItems = ({
  organizationSlug,
  locationSlug
}: {
  organizationSlug: string;
  locationSlug?: string;
}) => asPath(ROUTER_PATHS.ORGANIZATION_ITEMS, { organizationSlug }, { locationSlug });

export const toOrganizationProcesses = ({
  organizationSlug,
  locationSlug
}: {
  organizationSlug: string;
  locationSlug?: string;
}) => asPath(ROUTER_PATHS.ORGANIZATION_PROCESSES, { organizationSlug }, { locationSlug });

export const toOrganizationTalents = ({
  organizationSlug,
  locationSlug
}: {
  organizationSlug: string;
  locationSlug?: string;
}) => asPath(ROUTER_PATHS.ORGANIZATION_TALENTS, { organizationSlug }, { locationSlug });

export const toOrganizationSettings = ({
  organizationSlug,
  stepKey
}: {
  organizationSlug: string;
  stepKey: SETTINGS_STEP_KEYS;
}) => asPath(ROUTER_PATHS.ORGANIZATION_SETTINGS_GENERIC, { organizationSlug, stepKey }, {});

export const toOrganizationItemSettings = ({
  organizationSlug,
  locationSlug,
  locationType,
  stepKey
}: {
  organizationSlug: string;
  locationSlug: string;
  locationType: FolderType;
  stepKey: SETTINGS_STEP_KEYS;
}) =>
  asPath(
    ROUTER_PATHS.ORGANIZATION_ITEM_SETTINGS_GENERIC,
    { organizationSlug, stepKey, locationType: toOrganizationItemPath(locationType) },
    { locationSlug }
  );

export const toUserSettings = ({ userId, stepKey }: { userId: string; stepKey: SETTINGS_STEP_KEYS }) =>
  asPath(ROUTER_PATHS.USERS_SETTINGS_GENERIC, { userId, stepKey }, {});

export const toOrganizationSettingsTalentRegistrationForm = ({
  organizationSlug,
  activeTab = TALENT_REGISTRATION_FORM_TABS.EXTERNAL
}: {
  organizationSlug: string;
  activeTab?: TALENT_REGISTRATION_FORM_TABS;
}) => asPath(ROUTER_PATHS.ORGANIZATION_SETTINGS_TALENT_REGISTRATION_FORM, { organizationSlug, activeTab }, {});

export const toProcessSettingsForm = ({ processId, tabKey }: { processId: string; tabKey?: string }) =>
  asPath(
    ROUTER_PATHS.PROCESS_SETTINGS_FORM,
    { processId },
    tabKey
      ? {
          [EDIT_MODAL_PARAM]: tabKey
        }
      : {}
  );

export const toTalentTestSession = ({ talentId, testSessionId }: { talentId: string; testSessionId: string }) =>
  asPath(ROUTER_PATHS.TALENT_TEST_SESSION, { talentId, testSessionId }, {});

export const toTalent = ({ talentId }: { talentId: string }) => asPath(ROUTER_PATHS.TALENT, { talentId }, {});

export const toNewTalent = ({
  type,
  organizationSlug = 'open',
  locationSlug = 'open'
}: {
  type?: TalentCreateDTOType;
  organizationSlug?: string;
  locationSlug?: string;
}) =>
  asPath(ROUTER_PATHS.TALENTS, undefined, {
    [CREATE_MODAL_PARAM]: 'open',
    [DEFAULT_TYPE]: type,
    [DEFAULT_ORGANIZATION_SLUG]: organizationSlug,
    [DEFAULT_LOCATION_SLUG]: locationSlug
  });

export const toProcess = ({ processId }: { processId: string }) => asPath(ROUTER_PATHS.PROCESS, { processId }, {});

export const orgSettingsTabs = (
  t: TFunction,
  stepToLink: (step: SETTINGS_STEP_KEYS) => string,
  canViewProcessSettings?: boolean,
  canViewIdentificationAndNorms?: boolean,
  canViewBasicInfo?: boolean,
  canViewRolesAndUsers?: boolean,
  isMidotUser?: boolean
) =>
  [
    canViewBasicInfo && {
      stepKey: SETTINGS_STEP_KEYS.BASIC_INFO,
      label: t('orgSettings.tabs.basicInfo'),
      link: stepToLink(SETTINGS_STEP_KEYS.BASIC_INFO)
    },
    canViewRolesAndUsers && {
      stepKey: SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS,
      label: t('orgSettings.tabs.rolesAndUsers'),
      link: stepToLink(SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS)
    },
    canViewProcessSettings && {
      stepKey: SETTINGS_STEP_KEYS.PROCESS_SETTINGS,
      label: t('orgSettings.tabs.processSettings'),
      link: stepToLink(SETTINGS_STEP_KEYS.PROCESS_SETTINGS)
    },
    canViewIdentificationAndNorms && {
      stepKey: SETTINGS_STEP_KEYS.IDENTIFICATION_AND_NORMS,
      label: isMidotUser ? t('orgSettings.tabs.identificationNorms') : t('orgSettings.tabs.identification'),
      link: stepToLink(SETTINGS_STEP_KEYS.IDENTIFICATION_AND_NORMS)
    }
  ].filter((item): item is { stepKey: SETTINGS_STEP_KEYS; label: string; link: string } => Boolean(item));

export const suborgItemSettingsTabs = (
  t: TFunction,
  stepToLink: (step: SETTINGS_STEP_KEYS) => string,
  canViewProcessSettings?: boolean,
  canViewIdentificationAndNorms?: boolean,
  canViewBasicInfo?: boolean,
  canViewRolesAndUsers?: boolean
) =>
  [
    canViewBasicInfo && {
      stepKey: SETTINGS_STEP_KEYS.BASIC_INFO,
      label: t('orgSettings.tabs.basicInfo'),
      link: stepToLink(SETTINGS_STEP_KEYS.BASIC_INFO)
    },
    canViewRolesAndUsers && {
      stepKey: SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS,
      label: t('orgSettings.tabs.rolesAndUsers'),
      link: stepToLink(SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS)
    },
    canViewProcessSettings && {
      stepKey: SETTINGS_STEP_KEYS.PROCESS_SETTINGS,
      label: t('orgSettings.tabs.processSettings'),
      link: stepToLink(SETTINGS_STEP_KEYS.PROCESS_SETTINGS)
    },
    canViewIdentificationAndNorms && {
      stepKey: SETTINGS_STEP_KEYS.NORMS,
      label: t('orgSettings.tabs.norms'),
      link: stepToLink(SETTINGS_STEP_KEYS.NORMS)
    }
  ].filter((item): item is { stepKey: SETTINGS_STEP_KEYS; label: string; link: string } => Boolean(item));
